import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useMiniAppApi } from '../lib/context';
import { useAuthState } from '../lib/auth-context';
import { formatDate, formatTime, formatRelativeTime, getDateGroupKey } from '../lib/date-utils';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';

export default function RemindersView() {
  const api = useMiniAppApi();
  const auth = useAuthState();
  const userIdentifier = auth.session?.userId ?? auth.session?.telegramUserId;
  const canQuery =
    !!userIdentifier &&
    (auth.status === 'authenticated' ||
      auth.status === 'cached-session' ||
      auth.status === 'read-only');
  const timezone = 'UTC'; // TODO: Get from settings when available

  const remindersQuery = useQuery({
    queryKey: ['reminders', userIdentifier],
    queryFn: () => api.getReminders(userIdentifier || undefined),
    enabled: canQuery,
  });

  // Group reminders by date and status
  const groupedReminders = useMemo(() => {
    if (!remindersQuery.data) return new Map<string, typeof remindersQuery.data>();
    
    const groups = new Map<string, typeof remindersQuery.data>();
    
    // Separate by status first
    const pending = remindersQuery.data.filter(r => r.status === 'pending');
    const done = remindersQuery.data.filter(r => r.status === 'done');
    
    // Group pending reminders by date
    const pendingByDate = new Map<string, typeof remindersQuery.data>();
    for (const reminder of pending) {
      const dateKey = reminder.scheduledAt ? getDateGroupKey(reminder.scheduledAt, timezone) : 'Без даты';
      if (!pendingByDate.has(dateKey)) {
        pendingByDate.set(dateKey, []);
      }
      pendingByDate.get(dateKey)!.push(reminder);
    }
    
    // Sort pending by date (earliest first)
    const sortedPendingDates = Array.from(pendingByDate.entries()).sort((a, b) => {
      const dateA = a[1][0]?.scheduledAt ? new Date(a[1][0].scheduledAt).getTime() : 0;
      const dateB = b[1][0]?.scheduledAt ? new Date(b[1][0].scheduledAt).getTime() : 0;
      return dateA - dateB;
    });
    
    // Add pending groups in sorted order
    for (const [dateKey, reminders] of sortedPendingDates) {
      // Sort reminders within group by time
      reminders.sort((a, b) => {
        const dateA = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0;
        const dateB = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0;
        return dateA - dateB;
      });
      groups.set(`pending_${dateKey}`, reminders);
    }
    
    // Add done reminders if any
    if (done.length > 0) {
      groups.set('done', done);
    }
    
    return groups;
  }, [remindersQuery.data, timezone]);

  if (remindersQuery.isLoading) {
    return (
      <div className="card">
        <h2>Напоминания</h2>
        <LoadingSkeleton count={3} />
      </div>
    );
  }

  if (!userIdentifier) {
    return (
      <div className="card card--info">
        <h2>Напоминания</h2>
        {auth.status === 'needs-initdata' && (
          <>
            <p>Мы не получили данные Telegram. Откройте мини-приложение через кнопку в боте или нажмите «Проверить снова».</p>
            <button onClick={auth.retry} style={{ marginTop: '1rem' }}>
              Проверить снова
            </button>
          </>
        )}
        {auth.status === 'read-only' && (
          <p>Запустите мини-приложение внутри Telegram, чтобы увидеть ваши напоминания.</p>
        )}
        {auth.status === 'error' && (
          <>
            <p>Не удалось авторизоваться. {auth.message ?? 'Попробуйте позже или откройте приложение в Telegram.'}</p>
            <button onClick={auth.retry} style={{ marginTop: '1rem' }}>
              Повторить
            </button>
          </>
        )}
        {auth.status !== 'needs-initdata' &&
          auth.status !== 'read-only' &&
          auth.status !== 'error' && (
            <p>Для просмотра напоминаний требуется авторизация через Telegram.</p>
          )}
      </div>
    );
  }

  if (remindersQuery.isError) {
    const error = remindersQuery.error as AxiosError | Error;
    let title = 'Не удалось загрузить напоминания';
    let message =
      'Проверьте подключение к интернету и попробуйте снова. Если проблема сохраняется, используйте Telegram бота.';

    if ('isAxiosError' in (error as AxiosError) && (error as AxiosError).isAxiosError) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        title = 'Ошибка API';
        message = `Сервер ответил статусом ${axiosError.response.status}. Попробуйте позже.`;
      } else {
        title = 'Ошибка сети';
        message = 'Не удалось связаться с сервером. Проверьте подключение и попробуйте снова.';
      }
    }

    return (
      <div className="card">
        <ErrorState title={title} message={message} onRetry={() => remindersQuery.refetch()} />
      </div>
    );
  }

  const totalReminders = remindersQuery.data?.length || 0;
  const pendingCount = remindersQuery.data?.filter(r => r.status === 'pending').length || 0;
  const doneCount = remindersQuery.data?.filter(r => r.status === 'done').length || 0;
  const isReadOnly = auth.status === 'read-only' || auth.status === 'needs-initdata';

  return (
    <div className="stack">
      {isReadOnly && (
        <div className="card card--info">
          <h2>Режим просмотра</h2>
          <p>Этот интерфейс работает только в режиме чтения. Для создания, редактирования и удаления напоминаний используйте Telegram бота.</p>
        </div>
      )}

      {totalReminders > 0 && (
        <div className="card">
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e0e0e0' }}>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>Всего</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{totalReminders}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>Активных</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#FF9800' }}>{pendingCount}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>Выполнено</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#4CAF50' }}>{doneCount}</div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Напоминания</h2>
        {totalReminders === 0 ? (
          <EmptyState
            icon="⏰"
            title="У вас пока нет активных напоминаний"
            message={isReadOnly 
              ? "Создайте напоминание через Telegram бота, и оно появится здесь. Этот интерфейс работает только в режиме просмотра."
              : "Создайте напоминание через Telegram бота, и оно появится здесь."}
          />
        ) : (
          <div className="list">
            {Array.from(groupedReminders.entries()).map(([groupKey, reminders]) => {
              const isDoneGroup = groupKey === 'done';
              const dateKey = groupKey.replace(/^(pending|done)_/, '');
              
              return (
                <div key={groupKey} style={{ marginBottom: '1.5rem' }}>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    color: '#666', 
                    marginBottom: '0.5rem',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    {isDoneGroup ? '✅ Выполнено' : dateKey}
                  </div>
                  {reminders.map(reminder => (
                    <div key={reminder.id} className="list__item" style={{ 
                      opacity: isDoneGroup ? 0.7 : 1,
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div className="list__title" style={{ 
                          textDecoration: isDoneGroup ? 'line-through' : 'none',
                          color: isDoneGroup ? '#999' : 'inherit'
                        }}>
                          {reminder.title}
                        </div>
                        {reminder.scheduledAt && (
                          <div className="list__meta" style={{ 
                            display: 'flex', 
                            gap: '0.5rem', 
                            alignItems: 'center',
                            marginTop: '0.25rem',
                            flexWrap: 'wrap'
                          }}>
                            <span>📅 {formatDate(reminder.scheduledAt, timezone)}</span>
                            <span>🕐 {formatTime(reminder.scheduledAt, timezone)}</span>
                            {!isDoneGroup && (
                              <span style={{ color: '#FF9800', fontSize: '0.875rem', fontWeight: '500' }}>
                                ⏰ {formatRelativeTime(reminder.scheduledAt, timezone)}
                              </span>
                            )}
                          </div>
                        )}
                        {timezone && timezone !== 'UTC' && (
                          <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                            Часовой пояс: {timezone}
                          </div>
                        )}
                      </div>
                      <div className="list__actions">
                        <button 
                          disabled={isReadOnly} 
                          title={isReadOnly ? "Используйте Telegram бота для изменения напоминаний" : undefined}
                        >
                          {reminder.status === 'done' ? 'Вернуть' : 'Готово'}
                        </button>
                        <button 
                          disabled={isReadOnly} 
                          title={isReadOnly ? "Используйте Telegram бота для откладывания напоминаний" : undefined}
                        >
                          Отложить +15м
                        </button>
                        <button 
                          className="danger" 
                          disabled={isReadOnly} 
                          title={isReadOnly ? "Используйте Telegram бота для удаления напоминаний" : undefined}
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
